<template>
  <FlexCol gap-1>
    <Label :label="label" :required="required" :error="error" v-if="label" />

    <Input v-model="fileName" :error="error" :placeholder="props.placeholder" :disabled="props.disabled">
      <template #pre-icon v-if="fileName && isSavedFile">
        <span class="p-3 border-r cursor-pointer text-primary-600 hover:text-primary-700" @click="downloadFile" title="Baixar arquivo">
          <IconSolid icon="fa-download" />
        </span>
      </template>
      <template #pos-icon>
        <span class="p-3 border-l cursor-pointer" @click="triggerFileInput">
          <IconSolid icon="fa-file-arrow-up" class="mr-2" />
          Escolher Arquivo
        </span>
        <span class="p-3 border-l cursor-pointer" @click="clear()" v-if="fileName">
          <IconSolid icon="fa-xmark" />
        </span>
      </template>
    </Input>

    <input ref="fileInput" type="file" class="hidden" @change="handleFileChange" :disabled="props.disabled" />

    <Error :message="error" v-if="error" />
    <Card border rounded class="mt-3 overflow-hidden" v-if="preview">
      <FlexCol class="overflow-hidden relative">
        <div class="grow h-[115px] bg-[#F8FAFC] flex items-center justify-center">
          <img v-if="previewUrl" :src="previewUrl" class="h-full w-full object-cover" alt="Prévia da imagem" />
          <span v-else class="text-4xl text-gray-500 cursor-pointer" @click="triggerFileInput">
            <IconSolid icon="fa-file-arrow-up" />
          </span>
        </div>

        <div class="border-t p-1 bg-white text-center font-semibold text-xs w-full h-6">
          {{ props.previewText }}
        </div>
      </FlexCol>
    </Card>
  </FlexCol>
</template>

<script setup>
import { watch, ref, computed } from 'vue'

const props = defineProps({
  label: String,
  required: Boolean,
  disabled: Boolean,
  error: Array,
  modelValue: [File, Array, String],
  placeholder: String,
  preview: Boolean,
  previewText: String,
  imagePath: String,
})

const emits = defineEmits(['update:modelValue', 'update:imagePath'])

const fileInput = ref(null)
const fileName = ref('')
const previewUrl = ref(null)

const error = ref(props.error)

const baseUrl = process.env.VUE_APP_BASE_API_URL

const isSavedFile = computed(() => {
  return props.imagePath || (typeof props.modelValue === 'string' && props.modelValue)
})

const filePath = computed(() => {
  return props.imagePath || (typeof props.modelValue === 'string' ? props.modelValue : null)
})

const triggerFileInput = () => {
  fileInput.value?.click()
}

const clear = () => {
  const file = null
  fileName.value = null
  previewUrl.value = null
  emits('update:modelValue', file)
  emits('update:imagePath', null)
  if (fileInput.value) {
    fileInput.value.value = null
  }
}

const handleFileChange = (event) => {
  const file = event.target.files[0]
  if (file) {
    fileName.value = file.name
    emits('update:modelValue', file)

    if (file.type.startsWith('image/')) {
      previewUrl.value = URL.createObjectURL(file)
    } else {
      previewUrl.value = null
    }
  }
}

const downloadFile = () => {
  if (filePath.value && fileName.value) {
    const fileUrl = `${baseUrl}/uploads/${filePath.value}`
    const link = document.createElement('a')
    link.href = fileUrl
    link.download = fileName.value
    link.target = '_blank'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

watch(
  () => props.imagePath,
  (newPath) => {
    if (newPath) {
      const pathParts = newPath.split('/')
      fileName.value = pathParts[pathParts.length - 1]
    } else if (!props.modelValue) {
      fileName.value = ''
    }
  },
  { immediate: true },
)

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal instanceof File) {
      fileName.value = newVal.name
    } else if (typeof newVal === 'string' && newVal) {
      const pathParts = newVal.split('/')
      fileName.value = pathParts[pathParts.length - 1]
    }
  },
  { immediate: true },
)

watch(
  () => props.error,
  (newVal) => (error.value = newVal),
)

defineExpose({
  clear,
})
</script>
